import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit2, Trash2, Tag, ImagePlus, X, GripVertical, ArrowUp, ArrowDown, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOR_PRESETS = [
  { name: 'Pink', value: '#ec4899' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Orange', value: '#f59e0b' },
  { name: 'Green', value: '#10b981' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Indigo', value: '#6366f1' },
];

export default function EventTypesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    color: '#06b6d4',
    description: '',
    image: '',
    is_active: true,
    sort_order: 0,
    assigned_forms: []
  });
  
  // Forms assignment state
  const [availableForms, setAvailableForms] = useState([]);
  const [showAssignFormsDialog, setShowAssignFormsDialog] = useState(false);
  const [assigningEventType, setAssigningEventType] = useState(null);
  const [selectedFormIds, setSelectedFormIds] = useState([]);

  useEffect(() => {
    fetchEventTypes();
    fetchForms();
  }, []);

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/event-types`);
      // Sort by sort_order if it exists
      const sorted = response.data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setEventTypes(sorted);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/planning-forms`);
      setAvailableForms(response.data);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an event type name');
      return;
    }
    try {
      await axios.post(`${API}/event-types`, formData);
      toast.success('Event type created successfully');
      setShowDialog(false);
      resetForm();
      fetchEventTypes();
    } catch (error) {
      toast.error('Failed to create event type');
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an event type name');
      return;
    }
    try {
      await axios.put(`${API}/event-types/${selectedEventType.id}`, formData);
      toast.success('Event type updated successfully');
      setShowDialog(false);
      resetForm();
      fetchEventTypes();
    } catch (error) {
      toast.error('Failed to update event type');
    }
  };

  const handleDelete = async (eventTypeId) => {
    if (!window.confirm('Are you sure you want to delete this event type?')) return;
    try {
      await axios.delete(`${API}/event-types/${eventTypeId}`);
      toast.success('Event type deleted');
      fetchEventTypes();
    } catch (error) {
      toast.error('Failed to delete event type');
    }
  };

  const openEditDialog = (eventType) => {
    setSelectedEventType(eventType);
    setFormData({
      name: eventType.name,
      color: eventType.color || '#06b6d4',
      description: eventType.description || '',
      image: eventType.image || '',
      is_active: eventType.is_active,
      sort_order: eventType.sort_order || 0,
      assigned_forms: eventType.assigned_forms || []
    });
    setImagePreview(eventType.image || '');
    setEditMode(true);
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      color: '#06b6d4',
      description: '',
      image: '',
      is_active: true,
      sort_order: eventTypes.length,
      assigned_forms: []
    });
    setImagePreview('');
    setSelectedEventType(null);
    setEditMode(false);
  };
  
  // Form assignment functions
  const openAssignFormsDialog = (eventType) => {
    setAssigningEventType(eventType);
    setSelectedFormIds(eventType.assigned_forms || []);
    setShowAssignFormsDialog(true);
  };
  
  const toggleFormSelection = (formId) => {
    setSelectedFormIds(prev => 
      prev.includes(formId) 
        ? prev.filter(id => id !== formId)
        : [...prev, formId]
    );
  };
  
  const saveFormAssignments = async () => {
    try {
      await axios.put(`${API}/event-types/${assigningEventType.id}`, {
        assigned_forms: selectedFormIds
      });
      toast.success('Forms assigned successfully');
      setShowAssignFormsDialog(false);
      fetchEventTypes();
    } catch (error) {
      toast.error('Failed to assign forms');
    }
  };

  const moveEventType = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= eventTypes.length) return;

    const newEventTypes = [...eventTypes];
    const temp = newEventTypes[index];
    newEventTypes[index] = newEventTypes[newIndex];
    newEventTypes[newIndex] = temp;

    // Update sort_order for both swapped items
    try {
      await Promise.all([
        axios.put(`${API}/event-types/${newEventTypes[index].id}`, { sort_order: index }),
        axios.put(`${API}/event-types/${newEventTypes[newIndex].id}`, { sort_order: newIndex })
      ]);
      
      // Update local state with new sort orders
      newEventTypes[index] = { ...newEventTypes[index], sort_order: index };
      newEventTypes[newIndex] = { ...newEventTypes[newIndex], sort_order: newIndex };
      
      setEventTypes(newEventTypes);
      toast.success('Order updated');
    } catch (error) {
      toast.error('Failed to update order');
      fetchEventTypes(); // Refresh to get original order
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData({ ...formData, image: base64String });
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image: '' });
    setImagePreview('');
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="event-types-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="event-types-heading">Event Types</h1>
              <p className="text-muted-foreground">Manage your event categories for bookings</p>
            </div>
            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button 
                  onClick={openCreateDialog}
                  className="bg-neon-blue hover:bg-neon-blue/80 text-black" 
                  data-testid="create-event-type-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Event Type
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border border-border max-w-lg" data-testid="event-type-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-foreground">
                    {editMode ? 'Edit Event Type' : 'Create Event Type'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-background border-border text-foreground"
                      placeholder="e.g., Wedding, Corporate Event"
                      data-testid="event-type-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-foreground">Color</Label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_PRESETS.map((preset) => (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, color: preset.value })}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            formData.color === preset.value 
                              ? 'border-foreground scale-110' 
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: preset.value }}
                          title={preset.name}
                          data-testid={`color-${preset.name.toLowerCase()}`}
                        />
                      ))}
                      <div className="flex items-center gap-2 ml-2">
                        <Input
                          type="color"
                          value={formData.color}
                          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                          className="w-10 h-8 p-0 border-0 cursor-pointer"
                          data-testid="event-type-color-picker"
                        />
                        <span className="text-xs text-muted-foreground font-mono">{formData.color}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Description (Optional)</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-background border-border text-foreground"
                      placeholder="Brief description of this event type"
                      data-testid="event-type-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Image (Optional)</Label>
                    <div className="flex items-start gap-4">
                      {imagePreview ? (
                        <div className="relative">
                          <div className="w-24 h-24 rounded-lg overflow-hidden border border-border bg-muted">
                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                          </div>
                          <button
                            type="button"
                            onClick={removeImage}
                            className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600"
                            data-testid="remove-image-btn"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <label className="w-24 h-24 rounded-lg border-2 border-dashed border-border bg-muted flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                          <ImagePlus className="w-6 h-6 text-muted-foreground mb-1" />
                          <span className="text-xs text-muted-foreground">Upload</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            data-testid="event-type-image-input"
                          />
                        </label>
                      )}
                      <div className="flex-1 text-xs text-muted-foreground">
                        <p>Upload an image to represent this event type.</p>
                        <p className="mt-1">Max size: 2MB. Recommended: Square format.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowDialog(false)}
                      className="flex-1 border-border"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={editMode ? handleUpdate : handleCreate}
                      className="flex-1 bg-neon-blue hover:bg-neon-blue/80 text-black"
                      data-testid="save-event-type-btn"
                    >
                      {editMode ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : eventTypes.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-event-types-message">
              <Tag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No Event Types</h3>
              <p className="text-muted-foreground mb-4">Create your first event type to categorize bookings</p>
              <Button onClick={openCreateDialog} className="bg-neon-blue hover:bg-neon-blue/80 text-black">
                <Plus className="w-4 h-4 mr-2" />
                Create Event Type
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="event-types-list">
              <p className="text-sm text-muted-foreground">
                <GripVertical className="w-4 h-4 inline mr-1" />
                Use the arrows to reorder event types. This order will be reflected in dropdowns throughout the app.
              </p>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {eventTypes.map((eventType, index) => (
                  <div
                    key={eventType.id}
                    className="glass-card-hover rounded-2xl overflow-hidden"
                    data-testid={`event-type-item-${eventType.id}`}
                  >
                    {eventType.image ? (
                      <div className="h-32 w-full overflow-hidden relative">
                        <img 
                          src={eventType.image} 
                          alt={eventType.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveEventType(index, 'up')}
                            disabled={index === 0}
                            className="bg-black/50 backdrop-blur text-white hover:text-white hover:bg-black/70 disabled:opacity-30"
                            data-testid={`move-up-${eventType.id}`}
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveEventType(index, 'down')}
                            disabled={index === eventTypes.length - 1}
                            className="bg-black/50 backdrop-blur text-white hover:text-white hover:bg-black/70 disabled:opacity-30"
                            data-testid={`move-down-${eventType.id}`}
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="h-32 w-full flex items-center justify-center relative"
                        style={{ backgroundColor: `${eventType.color || '#06b6d4'}20` }}
                      >
                        <Tag className="w-12 h-12" style={{ color: eventType.color || '#06b6d4' }} />
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveEventType(index, 'up')}
                            disabled={index === 0}
                            className="bg-black/50 backdrop-blur text-white hover:text-white hover:bg-black/70 disabled:opacity-30"
                            data-testid={`move-up-${eventType.id}`}
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveEventType(index, 'down')}
                            disabled={index === eventTypes.length - 1}
                            className="bg-black/50 backdrop-blur text-white hover:text-white hover:bg-black/70 disabled:opacity-30"
                            data-testid={`move-down-${eventType.id}`}
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="text-xs text-muted-foreground font-mono bg-muted/30 px-2 py-0.5 rounded">
                            #{index + 1}
                          </div>
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: eventType.color || '#06b6d4' }}
                          />
                          <h3 className="font-heading text-xl font-semibold">{eventType.name}</h3>
                        </div>
                        {!eventType.is_active && (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">
                            Inactive
                          </span>
                        )}
                      </div>
                      {eventType.description && (
                        <p className="text-sm text-muted-foreground mb-4">{eventType.description}</p>
                      )}
                      <div className="flex gap-2 pt-4 border-t border-border">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(eventType)}
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`edit-event-type-${eventType.id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAssignFormsDialog(eventType)}
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`assign-forms-event-type-${eventType.id}`}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Forms ({eventType.assigned_forms?.length || 0})
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(eventType.id)}
                          className="text-red-500 hover:text-red-400"
                          data-testid={`delete-event-type-${eventType.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Assign Forms Dialog */}
      <Dialog open={showAssignFormsDialog} onOpenChange={setShowAssignFormsDialog}>
        <DialogContent className="bg-card border border-border max-w-lg" data-testid="assign-forms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-foreground flex items-center gap-2">
              <FileText className="w-6 h-6 text-neon-blue" />
              Assign Forms
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Select forms that clients with "{assigningEventType?.name}" events should fill out.
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[400px] overflow-y-auto">
            {availableForms.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No forms available. Create forms in Configuration → Forms first.
              </p>
            ) : (
              availableForms.map((form) => (
                <div
                  key={form.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedFormIds.includes(form.id)
                      ? 'border-neon-blue bg-neon-blue/10'
                      : 'border-border hover:border-muted-foreground bg-card'
                  }`}
                  onClick={() => toggleFormSelection(form.id)}
                  data-testid={`form-option-${form.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedFormIds.includes(form.id)}
                      onCheckedChange={() => toggleFormSelection(form.id)}
                      className="border-muted-foreground data-[state=checked]:bg-neon-blue data-[state=checked]:border-neon-blue"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{form.name}</h4>
                      {form.description && (
                        <p className="text-sm text-muted-foreground">{form.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {form.fields?.length || 0} field(s)
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowAssignFormsDialog(false)}
              className="flex-1 border-border"
            >
              Cancel
            </Button>
            <Button
              onClick={saveFormAssignments}
              className="flex-1 bg-neon-blue hover:bg-neon-blue/80 text-black"
              data-testid="save-form-assignments-btn"
            >
              Save ({selectedFormIds.length} selected)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
